/**
 * AboutTab — description, guidelines (editable by moderators), moderators.
 */

import React, { useState } from 'react';
import { ShieldCheck, Pencil, Check } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useCommunities } from '../context/CommunitiesContext';
import { VISIBILITY_META, ROLE_LABELS } from '../constants';
import { DEFAULT_RULES } from '../constants/seedData';

const avatarTone = { coral: 'bg-coral', yellow: 'bg-yellow', sage: 'bg-sage', blue: 'bg-blue' };

export default function AboutTab({ community, role }) {
  const { getMembers, updateRules } = useCommunities();
  const moderators = getMembers(community.id).filter(m => m.role === 'admin' || m.role === 'moderator');
  const rules = community.rules?.length ? community.rules : DEFAULT_RULES;
  const canEdit = role === 'admin' || role === 'moderator';

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rules.join('\n'));

  const handleSave = () => {
    updateRules(community.id, draft.split('\n').map(r => r.trim()).filter(Boolean));
    setEditing(false);
    toast('Guidelines updated');
  };

  return (
    <div className="u-container max-w-2xl space-y-8 py-8">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">About</h2>
        <p className="mt-2 text-sm leading-relaxed text-subtle">{community.description ?? community.tagline}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs text-subtle">
          <span className="rounded-full border border-line px-3 py-1">{community.category}</span>
          <span className="rounded-full border border-line px-3 py-1">{community.type === 'writer' ? 'Writer Community' : 'Reader Community'}</span>
          <span className="rounded-full border border-line px-3 py-1">{VISIBILITY_META[community.visibility]?.label}</span>
        </div>
        {community.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {community.tags.map(t => <span key={t} className="rounded-full bg-cream px-2.5 py-1 text-[11px] text-subtle">#{t}</span>)}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-ink">Community Guidelines</h2>
          {canEdit && !editing && (
            <button type="button" onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-xs font-semibold text-subtle hover:text-coral">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="mt-3">
            <Textarea value={draft} onChange={e => setDraft(e.target.value)} rows={6} className="rounded-xl border-line bg-cream text-sm" />
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="secondary" icon={false} onClick={() => setEditing(false)}>Cancel</Button>
              <Button variant="primary" icon={false} onClick={handleSave}><Check className="h-3.5 w-3.5" /> Save</Button>
            </div>
          </div>
        ) : (
          <ol className="mt-3 space-y-2">
            {rules.map((rule, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-ink">
                <span className="font-display font-semibold text-coral">{i + 1}.</span> {rule}
              </li>
            ))}
          </ol>
        )}
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold text-ink">Moderators</h2>
        <div className="mt-3 space-y-2">
          {moderators.map(m => (
            <div key={m.id} className="flex items-center gap-3">
              <span className={cx('grid h-9 w-9 place-items-center rounded-full font-display text-sm font-bold text-[#FFF7F0]', avatarTone[m.tone] ?? avatarTone.coral)}>{m.initial}</span>
              <p className="text-sm text-ink">{m.name}</p>
              <span className="inline-flex items-center gap-1 rounded-full bg-coral/10 px-2 py-0.5 text-[10px] font-semibold text-coral"><ShieldCheck className="h-3 w-3" /> {ROLE_LABELS[m.role]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
